/**
 * The failure table (plan 2026_09_28_quies_failure_handling.md decision 1; components/Failures/
 * spec.md § Cases): failure case → {severity, form, copy, actions, credit}. The only place a case
 * is described — the session machine emits a case id, `<Failure>` reads its row. Adding a case is
 * one row here.
 */
import type {FailureCopyKey} from './failureCopy'

export type FailureSeverity = 'info' | 'warning' | 'error'

/** Where the case is drawn. `card` and `line` are `<Failure>`'s own; the others are owned by the
 *  surface named in the row's note (the player, the sign-in card, a History row) or draw nothing. */
export type FailureForm = 'card' | 'line' | 'player' | 'inline' | 'history' | 'none'

export type FailureAction =
    | 'try-again'
    | 'rephrase'
    | 'resume'
    | 'voice-again'
    | 'listen'
    | 'continue'
    | 'begin'
    | 'sign-in'
    | 'check-again'
    | 'send-link'
    | 'use-email'

/** `on-begin`: returned if the person takes Begin, which ends the session (case 24). */
export type FailureCredit = 'kept' | 'returned' | 'on-begin' | 'free' | 'none'

/** The unit that brings the case's behaviour. F3 (stream watchdog) rows are described here already;
 *  their actions have no handler until that unit lands. */
export type FailureUnit = 'F1' | 'F2' | 'F3'

export interface FailureRow {
    /** The plan's case number. */
    n: string
    severity: FailureSeverity
    form: FailureForm
    title?: FailureCopyKey
    message: FailureCopyKey | null
    /** At most two; the one that finishes what was begun is last (primary). */
    actions: readonly FailureAction[]
    credit: FailureCredit
    /** Lines only: ms before the line clears itself; 0 = until the condition ends. */
    ttlMs?: number
    unit: FailureUnit
}

const LINE_TTL_MS = 8_000

export const FAILURES = {
    turn_failed: {n: '1', severity: 'error', form: 'card', message: 'turnFailed', actions: ['try-again'], credit: 'kept', unit: 'F1'},
    reconnecting: {n: '2', severity: 'info', form: 'line', message: 'reconnecting', actions: [], credit: 'kept', ttlMs: 0, unit: 'F1'},
    lost: {n: '2', severity: 'error', form: 'card', message: 'turnFailed', actions: ['try-again'], credit: 'kept', unit: 'F1'},
    refused: {n: '3', severity: 'info', form: 'card', title: 'refusalTitle', message: 'refusal', actions: ['rephrase', 'try-again'], credit: 'kept', unit: 'F1'},
    open_elsewhere: {n: '4', severity: 'info', form: 'line', message: 'openElsewhere', actions: [], credit: 'kept', ttlMs: LINE_TTL_MS, unit: 'F1'},
    service_down: {n: '5', severity: 'error', form: 'card', message: 'serviceDown', actions: ['try-again'], credit: 'kept', unit: 'F1'},
    // 6 — the gates. A 401 on the turn POST is the funnel's SignupGate (its own sign-in controls);
    // an account at 0 sessions meets the Offer, which draws the title and its own checkout buttons.
    signup_required: {n: '6', severity: 'info', form: 'inline', message: null, actions: ['sign-in'], credit: 'none', unit: 'F1'},
    no_sessions: {n: '6', severity: 'info', form: 'inline', title: 'sessionsUsedTitle', message: null, actions: [], credit: 'none', unit: 'F1'},
    voice_failed: {n: '7', severity: 'error', form: 'card', title: 'voiceFailedTitle', message: 'voiceFailed', actions: ['continue', 'voice-again'], credit: 'kept', unit: 'F2'},
    voice_stopped: {n: '8', severity: 'warning', form: 'card', message: 'voiceStopped', actions: ['continue', 'resume'], credit: 'kept', unit: 'F2'},
    voice_interrupted: {n: '9', severity: 'warning', form: 'card', message: 'voiceStopped', actions: ['continue', 'resume'], credit: 'kept', unit: 'F2'},
    // 9 — a restarted task's voicing not yet free to resume (its lease still live): the reattach
    // retries, and says so once the player has nothing left to play.
    voice_resuming: {n: '9', severity: 'info', form: 'line', message: 'voiceResuming', actions: [], credit: 'kept', ttlMs: 0, unit: 'F2'},
    stream_missing: {n: '10', severity: 'info', form: 'card', message: 'streamMissing', actions: ['listen'], credit: 'kept', unit: 'F3'},
    // 11 — not a failure: the player's own refused state, its light takes the focus.
    autoplay_refused: {n: '11', severity: 'info', form: 'player', message: 'tapToListen', actions: [], credit: 'kept', unit: 'F1'},
    file_failed: {n: '12', severity: 'error', form: 'card', message: 'fileFailed', actions: ['continue', 'voice-again'], credit: 'kept', unit: 'F2'},
    recording_gone: {n: '13', severity: 'info', form: 'history', message: 'recordingGone', actions: ['voice-again'], credit: 'free', unit: 'F2'},
    purchase_cancelled: {n: '14', severity: 'info', form: 'line', message: 'purchaseCancelled', actions: [], credit: 'none', ttlMs: LINE_TTL_MS, unit: 'F1'},
    purchase_pending: {n: '15', severity: 'warning', form: 'card', message: 'purchasePending', actions: ['check-again'], credit: 'none', unit: 'F1'},
    // 16 — a line under pricing: the plans stay, Select Plan is the retry.
    checkout_failed: {n: '16', severity: 'error', form: 'line', message: 'checkoutFailed', actions: [], credit: 'none', ttlMs: 0, unit: 'F1'},
    // 17 — nothing shown: the page reloads onto the current turn.
    stale_answer: {n: '17', severity: 'info', form: 'none', message: null, actions: [], credit: 'kept', unit: 'F1'},
    session_ended: {n: '18', severity: 'info', form: 'card', message: 'sessionEnded', actions: ['begin'], credit: 'kept', unit: 'F1'},
    busy: {n: '19a', severity: 'warning', form: 'card', message: 'busy', actions: ['try-again'], credit: 'kept', unit: 'F1'},
    too_long: {n: '19b', severity: 'error', form: 'card', message: 'tooLong', actions: ['begin'], credit: 'returned', unit: 'F1'},
    unsettled: {n: '20', severity: 'error', form: 'card', message: 'turnFailed', actions: ['try-again'], credit: 'kept', unit: 'F1'},
    agent_hung: {n: '21', severity: 'error', form: 'card', message: 'turnFailed', actions: ['try-again'], credit: 'kept', unit: 'F1'},
    not_saved: {n: '22', severity: 'error', form: 'card', message: 'notSaved', actions: ['try-again'], credit: 'kept', unit: 'F1'},
    // 23 — nothing shown: re-voiced in the background; recording_gone on replay if that fails.
    audio_unstored: {n: '23', severity: 'info', form: 'none', message: null, actions: [], credit: 'kept', unit: 'F2'},
    // 24 — Rephrase goes on with the same session; Begin ends it, and a charged one comes back then.
    meditation_refused: {n: '24', severity: 'info', form: 'card', title: 'refusalTitle', message: 'meditationRefused', actions: ['rephrase', 'begin'], credit: 'on-begin', unit: 'F1'},
    // 24 for a session that was never charged (a first turn, a free one): nothing to say of credit.
    meditation_refused_uncharged: {n: '24', severity: 'info', form: 'card', title: 'refusalTitle', message: 'meditationRefusedUncharged', actions: ['rephrase', 'begin'], credit: 'kept', unit: 'F1'},
    // 25, 26 — the sign-in card's own error line and controls (core/auth-ui, authErrorCopy).
    link_expired: {n: '25', severity: 'error', form: 'inline', message: 'linkExpired', actions: ['send-link'], credit: 'none', unit: 'F1'},
    oauth_failed: {n: '26', severity: 'error', form: 'inline', message: 'oauthFailed', actions: ['use-email'], credit: 'none', unit: 'F1'},
    offline: {n: '27', severity: 'info', form: 'card', message: 'offline', actions: ['try-again'], credit: 'kept', unit: 'F1'},
    voice_changed: {n: '28', severity: 'info', form: 'line', message: 'voiceChanged', actions: [], credit: 'kept', ttlMs: LINE_TTL_MS, unit: 'F1'},
    voice_returned: {n: '29', severity: 'error', form: 'card', message: 'voiceReturned', actions: ['continue'], credit: 'returned', unit: 'F2'},
    // 29 for a session that was never charged (a free or unrecorded one): nothing to say of credit.
    voice_failed_again: {n: '29', severity: 'error', form: 'card', message: 'voiceFailedAgain', actions: ['continue'], credit: 'kept', unit: 'F2'},
    // Resume or Voice again asked too often this hour (the server's voicing limit, a 429).
    voice_limited: {n: 'limit', severity: 'info', form: 'line', message: 'voiceLimited', actions: [], credit: 'kept', ttlMs: LINE_TTL_MS, unit: 'F2'},
} as const satisfies Record<string, FailureRow>

export type FailureCase = keyof typeof FAILURES

export function failureRow(failure: FailureCase): FailureRow {
    return FAILURES[failure]
}

export const ACTION_LABEL: Record<FailureAction, FailureCopyKey> = {
    'try-again': 'tryAgain',
    rephrase: 'rephrase',
    resume: 'resume',
    'voice-again': 'voiceAgain',
    listen: 'listen',
    continue: 'continue',
    begin: 'begin',
    'sign-in': 'signIn',
    'check-again': 'checkAgain',
    'send-link': 'sendLink',
    'use-email': 'useEmail',
}

/** Gates and a new session are the lit shape (decisions.md §5: the one filled shape on a screen
 *  where nothing plays); otherwise the last action is primary and any before it quiet. */
const LIT_ACTIONS: ReadonlySet<FailureAction> = new Set(['begin', 'sign-in'])

export type ActionEmphasis = 'lit' | 'primary' | 'quiet'

export function actionEmphasis(actions: readonly FailureAction[], index: number): ActionEmphasis {
    if (LIT_ACTIONS.has(actions[index])) return 'lit'
    return index === actions.length - 1 ? 'primary' : 'quiet'
}

/** Error cards interrupt (`alert`); info and warning cards, and every line, are polite. */
export function roleFor(severity: FailureSeverity, form: FailureForm): 'alert' | 'status' {
    return severity === 'error' && form === 'card' ? 'alert' : 'status'
}

/**
 * A run's `error` event → its case; the server returns the credit on the same answer. The
 * event's `message` is a server diagnostic, never shown. `code` comes from an agent that names its
 * failures; the messages are what an agent from before `code` sends. Without `code`, a turn too
 * long reads as the model busy: the credit is kept and the card never says it was returned.
 */
export function classifyRunError(data: {message?: unknown; code?: unknown} | null | undefined): FailureCase {
    const code = typeof data?.code === 'string' ? data.code : null
    if (code === 'overloaded') return 'busy'
    if (code === 'context_too_long') return 'too_long'
    if (code === 'max_iterations') return 'unsettled'
    if (code === 'idle_timeout') return 'agent_hung'
    const message = typeof data?.message === 'string' ? data.message : ''
    if (/did not complete within \d+ iterations/.test(message)) return 'unsettled'
    if (/^Turn timeout$/.test(message)) return 'agent_hung'
    if (/temporarily unavailable/i.test(message)) return 'busy'
    return 'turn_failed'
}
